<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";

import {
  attachHandoverDocument,
  fetchCheckSheetPdf,
  fetchHandoverCertificate,
  getDownloadUrl,
  getHandoverHistory,
  getProjectHandover,
  issueFinalCertificate,
  listCheckSheets,
  listDefects,
  listFormTemplates,
  removeHandoverDocument,
  reopenHandover,
  signOffHandover,
  uploadFile,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const router = useRouter();
const queryClient = useQueryClient();
const { selectedProjectId } = storeToRefs(useProjectStore());

const { data, isPending, isError, error } = useQuery({
  queryKey: ["handover", selectedProjectId],
  queryFn: () => getProjectHandover(selectedProjectId.value as string),
});

const { data: history } = useQuery({
  queryKey: ["handover-history", selectedProjectId],
  queryFn: () => getHandoverHistory(selectedProjectId.value as string),
});

async function openCertificate(
  kind: "practical_completion" | "final",
): Promise<void> {
  actionError.value = null;
  try {
    const blob = await fetchHandoverCertificate(selectedProjectId.value as string, kind);
    window.open(URL.createObjectURL(blob), "_blank");
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  }
}

// ---- Documentation turnover pack ----
const DOC_KINDS = [
  { value: "om_manual", label: "O&M manual" },
  { value: "as_built", label: "As-built drawing" },
  { value: "warranty", label: "Warranty" },
  { value: "compliance_cert", label: "Compliance certificate" },
  { value: "other", label: "Other" },
];
const docKindLabel = (k: string): string =>
  DOC_KINDS.find((d) => d.value === k)?.label ?? k;

const docKind = ref("om_manual");
const docTitle = ref("");
const uploading = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);

async function onUpload(e: Event): Promise<void> {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file) return;
  uploading.value = true;
  actionError.value = null;
  try {
    const fileId = await uploadFile(file, "handover_doc");
    await attachHandoverDocument(selectedProjectId.value as string, {
      kind: docKind.value,
      title: docTitle.value.trim() || file.name,
      fileId,
      mimeType: file.type || undefined,
    });
    docTitle.value = "";
    if (fileInput.value) fileInput.value.value = "";
    await queryClient.invalidateQueries({ queryKey: ["handover"] });
  } catch (err) {
    actionError.value = err instanceof Error ? err.message : String(err);
  } finally {
    uploading.value = false;
  }
}

async function openDoc(fileId: string): Promise<void> {
  actionError.value = null;
  try {
    window.open(await getDownloadUrl(fileId), "_blank");
  } catch (err) {
    actionError.value = err instanceof Error ? err.message : String(err);
  }
}

function removeDoc(documentId: string): void {
  if (!window.confirm("Remove this document from the pack?")) return;
  void act(() =>
    removeHandoverDocument(selectedProjectId.value as string, documentId),
  );
}

// ---- Generated records auto-included in the pack ----
const { data: checkSheets } = useQuery({
  queryKey: ["check-sheets", selectedProjectId],
  queryFn: () => listCheckSheets(selectedProjectId.value),
});
const { data: templates } = useQuery({
  queryKey: ["form-templates"],
  queryFn: () => listFormTemplates(),
});
const templateName = (id: string): string =>
  (templates.value ?? []).find((t) => t.id === id)?.name ?? id;

// Completed ITP records (signed off or approved) are the QA evidence handed over.
const completedSheets = computed(() =>
  (checkSheets.value ?? []).filter(
    (c) => c.status === "approved" || c.status === "signed_off",
  ),
);

async function openCheckSheetPdf(checkSheetId: string): Promise<void> {
  actionError.value = null;
  try {
    const blob = await fetchCheckSheetPdf(checkSheetId);
    window.open(URL.createObjectURL(blob), "_blank");
  } catch (err) {
    actionError.value = err instanceof Error ? err.message : String(err);
  }
}

// The punch list = every open defect (snags are the flagged subset). Sign-off
// is blocked until they're all closed.
const { data: openDefects } = useQuery({
  queryKey: ["defects", selectedProjectId, "open"],
  queryFn: () => listDefects(selectedProjectId.value, "open"),
});

const ready = computed(() => (data.value?.open_defects ?? 0) === 0);

const today = new Date().toISOString().slice(0, 10);
const pcDate = ref(today);
const dlpMonths = ref(12);
const signNote = ref("");

// Final certificate can be issued once handover is signed off and no DLP
// defects remain open.
const canFinalise = computed(
  () => !!data.value?.signed_off && (data.value?.open_dlp_defects ?? 0) === 0,
);

const busy = ref(false);
const actionError = ref<string | null>(null);

async function act(fn: () => Promise<void>): Promise<void> {
  busy.value = true;
  actionError.value = null;
  try {
    await fn();
    await queryClient.invalidateQueries({ queryKey: ["handover"] });
    await queryClient.invalidateQueries({ queryKey: ["handover-history"] });
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}

function signOff(): void {
  if (!ready.value) return;
  void act(() =>
    signOffHandover(selectedProjectId.value as string, {
      note: signNote.value.trim() || undefined,
      practicalCompletionDate: pcDate.value || undefined,
      dlpMonths: Number(dlpMonths.value),
    }),
  );
}
function finalise(): void {
  if (!canFinalise.value) return;
  const note = window.prompt("Final certificate note (optional):") ?? undefined;
  void act(() =>
    issueFinalCertificate(selectedProjectId.value as string, note || undefined),
  );
}
function reopen(): void {
  const reason = window.prompt("Reason for reopening handover?") ?? undefined;
  if (reason && reason.trim())
    void act(() => reopenHandover(selectedProjectId.value as string, reason.trim()));
}
function openDefect(id: string): void {
  void router.push({
    name: "defect-detail",
    params: { projectId: selectedProjectId.value, defectId: id },
  });
}

const fmt = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "—";
</script>

<template>
  <PageLayout>
    <h1 class="mb-1 text-xl font-semibold">
      Handover
    </h1>
    <p class="mb-4 text-sm text-neutral-500">
      Close-out: clear the snag list, then sign off practical completion.
    </p>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data"
      class="grid gap-6"
    >
      <!-- Readiness / sign-off -->
      <section
        class="rounded-xl border p-6"
        :class="data.signed_off
          ? 'border-semantic-success/40 bg-semantic-success/5'
          : ready
            ? 'border-semantic-success/40 bg-neutral-0'
            : 'border-semantic-warning/40 bg-neutral-0'"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold">
              <span v-if="data.signed_off">✓ Handover signed off</span>
              <span v-else-if="ready">Ready for handover</span>
              <span v-else>{{ data.open_defects }} open defect(s) to clear</span>
            </h2>
            <p class="mt-1 text-sm text-neutral-500">
              <span v-if="data.signed_off">
                Accepted {{ fmt(data.signed_off_at) }}
                <span v-if="data.signed_off_by_name"> by {{ data.signed_off_by_name }}</span>
                <span v-if="data.note"> · "{{ data.note }}"</span>
              </span>
              <span v-else>
                {{ data.open_defects }} open defect(s)
                <span v-if="data.open_snags"> · {{ data.open_snags }} flagged as snags</span>
              </span>
            </p>
          </div>
          <div
            v-if="data.signed_off"
            class="flex shrink-0 gap-2"
          >
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium hover:bg-neutral-50"
              @click="openCertificate('practical_completion')"
            >
              Certificate (PDF)
            </button>
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium hover:bg-neutral-50"
              :disabled="busy"
              @click="reopen"
            >
              Reopen
            </button>
          </div>
        </div>

        <!-- Sign-off form: practical completion + DLP -->
        <div
          v-if="!data.signed_off"
          class="mt-4 border-t border-neutral-100 pt-4"
        >
          <p
            v-if="!ready"
            class="text-sm text-semantic-warning"
          >
            Sign-off is blocked until every open defect is closed.
          </p>
          <div
            v-else
            class="grid gap-3"
          >
            <div class="grid gap-3 sm:grid-cols-3">
              <label class="grid gap-1 text-sm">
                <span class="text-neutral-500">Practical completion date</span>
                <input
                  v-model="pcDate"
                  type="date"
                  class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
                >
              </label>
              <label class="grid gap-1 text-sm">
                <span class="text-neutral-500">DLP (months)</span>
                <input
                  v-model.number="dlpMonths"
                  type="number"
                  min="0"
                  max="120"
                  class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
                >
              </label>
              <label class="grid gap-1 text-sm">
                <span class="text-neutral-500">Note (optional)</span>
                <input
                  v-model="signNote"
                  type="text"
                  class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
                >
              </label>
            </div>
            <div>
              <button
                type="button"
                class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-40"
                :disabled="busy"
                @click="signOff"
              >
                Sign off handover
              </button>
            </div>
          </div>
        </div>
        <p
          v-if="actionError"
          class="mt-3 text-sm text-semantic-danger"
        >
          {{ actionError }}
        </p>
      </section>

      <!-- DLP + final certificate (after practical completion) -->
      <section
        v-if="data.signed_off && data.practical_completion_date"
        class="rounded-xl border border-neutral-200 bg-neutral-0 p-6"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="font-semibold">
              Defects Liability Period
            </h2>
            <p class="mt-1 text-sm text-neutral-500">
              Practical completion {{ data.practical_completion_date }} ·
              {{ data.dlp_months }} month DLP ·
              ends <strong>{{ data.dlp_ends_on ?? "—" }}</strong>
            </p>
            <p class="mt-2 text-sm">
              <span
                :class="data.open_dlp_defects > 0 ? 'text-semantic-warning' : 'text-semantic-success'"
              >{{ data.open_dlp_defects }} open DLP defect(s)</span>
              <span class="text-neutral-400"> (raised since practical completion)</span>
            </p>
          </div>
          <div class="shrink-0">
            <div
              v-if="data.final_certificate_issued"
              class="text-right text-sm"
            >
              <p class="font-semibold text-semantic-success">
                ✓ Final certificate issued
              </p>
              <p class="text-neutral-500">
                {{ fmt(data.final_certificate_at) }}
                <span v-if="data.final_certificate_by_name"> · {{ data.final_certificate_by_name }}</span>
              </p>
              <button
                type="button"
                class="mt-2 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
                @click="openCertificate('final')"
              >
                Certificate (PDF)
              </button>
            </div>
            <button
              v-else
              type="button"
              class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-40"
              :disabled="!canFinalise || busy"
              :title="canFinalise ? '' : 'Clear all open DLP defects first'"
              @click="finalise"
            >
              Issue final certificate
            </button>
          </div>
        </div>
        <p
          v-if="!canFinalise && !data.final_certificate_issued"
          class="mt-3 text-sm text-semantic-warning"
        >
          The final certificate is blocked until all DLP defects are closed.
        </p>
      </section>

      <!-- Punch list: every open defect (snags highlighted) -->
      <section class="rounded-xl bg-neutral-0 shadow overflow-hidden">
        <h2 class="px-6 pb-3 pt-6 font-semibold">
          Open defects ({{ openDefects?.length ?? 0 }})
        </h2>
        <p
          v-if="(openDefects?.length ?? 0) === 0"
          class="px-6 pb-6 text-sm text-neutral-400"
        >
          No open defects — the punch list is clear.
        </p>
        <table
          v-else
          class="w-full text-sm"
        >
          <thead class="bg-neutral-50 text-left text-neutral-500">
            <tr>
              <th class="px-4 py-2 font-medium">
                Description
              </th>
              <th class="px-4 py-2 font-medium">
                Category
              </th>
              <th class="px-4 py-2 font-medium">
                Assigned
              </th>
              <th class="px-4 py-2 font-medium">
                Due
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="s in openDefects ?? []"
              :key="s.id"
              class="cursor-pointer border-t border-neutral-100 hover:bg-neutral-50"
              @click="openDefect(s.id)"
            >
              <td class="px-4 py-2">
                {{ s.description }}
                <span
                  v-if="s.is_snag"
                  class="ml-1.5 rounded bg-semantic-warning/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-semantic-warning"
                >Snag</span>
              </td>
              <td class="px-4 py-2 capitalize">
                {{ s.category }}
              </td>
              <td class="px-4 py-2 whitespace-nowrap">
                {{ s.assigned_to_name ?? "—" }}
              </td>
              <td class="px-4 py-2 whitespace-nowrap text-neutral-500">
                {{ s.target_date ?? "—" }}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Documentation turnover pack (O&M) -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-1 font-semibold">
          Documentation pack
        </h2>
        <p class="mb-4 text-xs text-neutral-400">
          O&amp;M manuals, as-builts, warranties and compliance certificates
          handed over at completion.
        </p>

        <div class="mb-4 flex flex-wrap items-end gap-2">
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Type</span>
            <select
              v-model="docKind"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
              <option
                v-for="k in DOC_KINDS"
                :key="k.value"
                :value="k.value"
              >
                {{ k.label }}
              </option>
            </select>
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Title (optional)</span>
            <input
              v-model="docTitle"
              type="text"
              placeholder="Defaults to the file name"
              class="w-56 rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
          </label>
          <label
            class="cursor-pointer rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on"
            :class="uploading ? 'opacity-50' : ''"
          >
            {{ uploading ? "Uploading…" : "+ Add document" }}
            <input
              ref="fileInput"
              type="file"
              class="hidden"
              :disabled="uploading"
              @change="onUpload"
            >
          </label>
        </div>

        <p
          v-if="data.documents.length === 0"
          class="text-sm text-neutral-400"
        >
          No documents added yet.
        </p>
        <ul
          v-else
          class="divide-y divide-neutral-100"
        >
          <li
            v-for="d in data.documents"
            :key="d.document_id"
            class="flex items-center gap-3 py-2 text-sm"
          >
            <span class="rounded bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">
              {{ docKindLabel(d.kind) }}
            </span>
            <button
              type="button"
              class="text-brand-primary hover:underline"
              @click="openDoc(d.file_id)"
            >
              {{ d.title }}
            </button>
            <span class="ml-auto text-xs text-neutral-400">
              {{ d.attached_by_name ?? "—" }} · {{ fmt(d.attached_at) }}
            </span>
            <button
              type="button"
              class="text-neutral-300 hover:text-semantic-danger"
              title="Remove"
              :disabled="busy"
              @click="removeDoc(d.document_id)"
            >
              ✕
            </button>
          </li>
        </ul>
      </section>

      <!-- Generated records auto-included in the pack -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-1 font-semibold">
          Generated records
        </h2>
        <p class="mb-4 text-xs text-neutral-400">
          Documents the system produces — included in the turnover pack automatically.
        </p>
        <ul class="divide-y divide-neutral-100">
          <li
            v-if="data.signed_off"
            class="flex items-center gap-3 py-2 text-sm"
          >
            <span class="rounded bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">Certificate</span>
            <button
              type="button"
              class="text-brand-primary hover:underline"
              @click="openCertificate('practical_completion')"
            >
              Certificate of Practical Completion
            </button>
          </li>
          <li
            v-if="data.final_certificate_issued"
            class="flex items-center gap-3 py-2 text-sm"
          >
            <span class="rounded bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">Certificate</span>
            <button
              type="button"
              class="text-brand-primary hover:underline"
              @click="openCertificate('final')"
            >
              Final Certificate
            </button>
          </li>
          <li
            v-for="c in completedSheets"
            :key="c.id"
            class="flex items-center gap-3 py-2 text-sm"
          >
            <span class="rounded bg-neutral-100 px-2 py-0.5 text-xs capitalize text-neutral-600">
              ITP · {{ c.status.replace("_", " ") }}
            </span>
            <button
              type="button"
              class="text-brand-primary hover:underline"
              @click="openCheckSheetPdf(c.id)"
            >
              {{ templateName(c.template_id) }}
            </button>
          </li>
        </ul>
        <p
          v-if="!data.signed_off && !data.final_certificate_issued && completedSheets.length === 0"
          class="text-sm text-neutral-400"
        >
          No generated records yet — completed ITP check sheets and certificates
          appear here automatically.
        </p>
      </section>

      <!-- Handover audit trail -->
      <section
        v-if="(history?.length ?? 0) > 0"
        class="rounded-xl border border-neutral-200 bg-neutral-0 p-6"
      >
        <h2 class="mb-3 font-semibold">
          Handover history
        </h2>
        <ol class="grid gap-1 text-sm">
          <li
            v-for="h in history ?? []"
            :key="h.seq"
            class="flex flex-wrap gap-x-2 text-neutral-600"
          >
            <span class="font-medium text-neutral-800">{{ h.label }}</span>
            <span class="text-neutral-400">·</span>
            <span>{{ h.actor_name ?? "—" }}</span>
            <span class="text-neutral-400">·</span>
            <span class="text-neutral-400">{{ fmt(h.at) }}</span>
          </li>
        </ol>
      </section>
    </div>
  </PageLayout>
</template>
